import { MaskLine, Rise } from "@/components/motion/Reveal";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowLeft, ArrowUpRight, Mail, Phone } from "lucide-react";
import { PROFILES } from "@/data/profiles";
import Seo from "@/seo/Seo";
import { ldPerson, ldBreadcrumb } from "@/seo/seoConfig";

export default function PartnerProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const partner = PROFILES.find((p) => p.slug === slug);
  const other = PROFILES.find((p) => p.slug !== slug);

  useEffect(() => {
    if (!partner) navigate("/team", { replace: true });
  }, [partner, navigate]);
  if (!partner) return null;

  return (
    <div className="pt-40">
      <Seo
        title={`${partner.name} · ${partner.role} · ${partner.focus}`}
        description={`${partner.name} — ${partner.role} at RPLC UAE. ${partner.bio[0]}`}
        path={`/team/${partner.slug}`}
        type="profile"
        image={partner.img}
        keywords={[partner.name, partner.name + " lawyer", ...partner.focus.split("·").map((s) => s.trim() + " lawyer"), "Rane Pingle Law Chambers"]}
        jsonLd={[
          ldPerson(partner),
          ldBreadcrumb([
            { name: "Home", path: "/" },
            { name: "Team", path: "/team" },
            { name: partner.name, path: `/team/${partner.slug}` },
          ]),
        ]}
      />
      {/* HEADER */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <Rise>
          <Link to="/team" className="chapter-num inline-flex items-center gap-2 text-[#A19D94] hover:text-[#F4F1EB] transition-colors">
            <ArrowLeft size={14} strokeWidth={1.25} /> The bench
          </Link>
        </Rise>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <Rise>
              <div className="chapter-num text-[#C84C35]">{partner.role}</div>
            </Rise>
            <h1 className="font-serif tracking-tight leading-[0.9] text-[15vw] lg:text-[9vw] mt-6">
              <MaskLine delay={0.15}>{partner.firstName}</MaskLine>
              <MaskLine delay={0.32}><span className="italic-accent">{partner.lastName}</span></MaskLine>
            </h1>
            <Rise delay={0.5}>
              <p className="mt-8 text-[#A19D94] text-xl lg:text-2xl leading-relaxed max-w-2xl italic-accent">
                {partner.focus}
              </p>
            </Rise>
          </div>
          <div className="lg:col-span-5">
            <Rise delay={0.2}>
              <div className="relative overflow-hidden border border-[#141414]">
                <img src={partner.img} alt={partner.name} className="w-full h-[520px] lg:h-[620px] object-cover object-top photo-mono" />
                <div className="absolute top-4 left-4 chapter-num text-[#F4F1EB]">{partner.slug === "raunak-rane" ? "01 · 02" : "02 · 02"}</div>
              </div>
            </Rise>
          </div>
        </div>

        <Rise delay={0.4}>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#141414] border border-[#141414]">
            <a href={`mailto:${partner.email}`} className="bg-[#050505] p-6 lg:p-8 flex items-start gap-4 hover:bg-[#0A0A0A] transition-colors duration-500">
              <Mail size={18} strokeWidth={1.25} className="text-[#C84C35] mt-1" />
              <div>
                <div className="chapter-num">Direct</div>
                <div className="mt-2 text-[#F4F1EB]">{partner.email}</div>
              </div>
            </a>
            <a href={`tel:${partner.phone.replace(/\s+/g, "")}`} className="bg-[#050505] p-6 lg:p-8 flex items-start gap-4 hover:bg-[#0A0A0A] transition-colors duration-500">
              <Phone size={18} strokeWidth={1.25} className="text-[#C84C35] mt-1" />
              <div>
                <div className="chapter-num">Phone</div>
                <div className="mt-2 text-[#F4F1EB]">{partner.phone}</div>
              </div>
            </a>
            <div className="bg-[#050505] p-6 lg:p-8">
              <div className="chapter-num">Admitted</div>
              <div className="mt-2 text-[#F4F1EB]">{partner.admissions.join(" · ")}</div>
            </div>
          </div>
        </Rise>
      </section>

      {/* BIOGRAPHY */}
      <section className="mt-32 border-y border-[#141414]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-3">
            <Rise><div className="chapter-num text-[#C84C35]">§01</div></Rise>
            <Rise delay={0.05}><div className="mt-4 eyebrow">The practice</div></Rise>
          </div>
          <div className="lg:col-span-9 space-y-6">
            {partner.bio.map((p, i) => (
              <Rise key={i} delay={i * 0.04}>
                <p className="font-serif text-xl lg:text-2xl leading-[1.5] text-[#F4F1EB]">
                  {i === 0 ? (
                    <>
                      <span className="font-serif text-6xl lg:text-7xl float-left leading-[0.85] mr-3 mt-1 text-[#C84C35] italic-accent">{p[0]}</span>
                      {p.slice(1)}
                    </>
                  ) : (
                    p
                  )}
                </p>
              </Rise>
            ))}
          </div>
        </div>
      </section>

      {/* EDUCATION */}
      <section className="py-24 border-b border-[#141414]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-3">
            <Rise><div className="chapter-num text-[#C84C35]">§02</div></Rise>
            <Rise delay={0.05}><div className="mt-4 eyebrow">Education</div></Rise>
          </div>
          <div className="lg:col-span-9">
            <div className="space-y-6">
              {partner.education.map((e, i) => (
                <Rise key={i} delay={i * 0.05}>
                  <div className="grid grid-cols-12 gap-6 pb-6 border-b border-[#141414]">
                    <div className="col-span-3 lg:col-span-2 chapter-num text-[#66635C] pt-2">{e.year}</div>
                    <div className="col-span-9 lg:col-span-10">
                      <div className="font-serif text-2xl lg:text-3xl leading-tight">{e.degree}</div>
                      <div className="mt-1 italic-accent text-[#A19D94]">{e.school}</div>
                    </div>
                  </div>
                </Rise>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* NOTABLE MATTERS */}
      <section className="py-24 border-b border-[#141414]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-3">
            <Rise><div className="chapter-num text-[#C84C35]">§03</div></Rise>
            <Rise delay={0.05}><div className="mt-4 eyebrow">Notable matters</div></Rise>
            <Rise delay={0.1}>
              <p className="mt-4 text-sm text-[#66635C] leading-relaxed max-w-xs">
                Client names withheld under counsel duties; details rendered indicative.
              </p>
            </Rise>
          </div>
          <div className="lg:col-span-9">
            {partner.matters.map((m, i) => (
              <Rise key={i} delay={i * 0.05}>
                <div className="grid grid-cols-12 gap-6 py-6 border-b border-[#141414]">
                  <div className="col-span-2 lg:col-span-1 chapter-num text-[#66635C] pt-2">{String(i + 1).padStart(2, "0")}</div>
                  <div className="col-span-10 lg:col-span-3">
                    <div className="chapter-num text-[#C84C35]">{m.tag}</div>
                    <div className="chapter-num text-[#66635C] mt-2">{m.year}</div>
                  </div>
                  <div className="col-span-12 lg:col-span-8">
                    <div className="font-serif text-xl lg:text-2xl leading-tight">{m.title}</div>
                    <p className="mt-3 text-[#A19D94] leading-relaxed">{m.body}</p>
                  </div>
                </div>
              </Rise>
            ))}
          </div>
        </div>
      </section>

      {/* SPEAKING & PUBLICATIONS */}
      {partner.speaking && partner.speaking.length > 0 && (
        <section className="py-24 border-b border-[#141414]">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-3">
              <Rise><div className="chapter-num text-[#C84C35]">§04</div></Rise>
              <Rise delay={0.05}><div className="mt-4 eyebrow">Speaking & writing</div></Rise>
            </div>
            <div className="lg:col-span-9 grid grid-cols-1 md:grid-cols-2 gap-8">
              {partner.speaking.map((s, i) => (
                <Rise key={i} delay={i * 0.05}>
                  <div className="border-l border-[#C84C35] pl-6 py-2 h-full">
                    <div className="chapter-num text-[#66635C]">{s.year} · {s.where}</div>
                    <div className="mt-3 font-serif text-2xl leading-tight">{s.title}</div>
                    <div className="mt-2 text-sm text-[#A19D94]">{s.venue}</div>
                  </div>
                </Rise>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MEMBERSHIPS */}
      <section className="py-24 border-b border-[#141414]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-3">
            <Rise><div className="chapter-num text-[#C84C35]">§05</div></Rise>
            <Rise delay={0.05}><div className="mt-4 eyebrow">Memberships & recognition</div></Rise>
          </div>
          <div className="lg:col-span-9 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            {partner.memberships.map((m, i) => (
              <Rise key={i} delay={i * 0.03}>
                <div className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-[#C84C35] mt-2.5 shrink-0" />
                  <span className="text-[#A19D94]">{m}</span>
                </div>
              </Rise>
            ))}
          </div>
        </div>
      </section>

      {/* NEXT PARTNER */}
      {other && (
        <section className="border-t border-[#141414]">
          <Link to={`/team/${other.slug}`} className="block group py-24">
            <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
              <div className="chapter-num text-[#66635C]">Next · Managing Partner</div>
              <div className="mt-6 flex items-start justify-between gap-8">
                <h2 className="font-serif text-5xl lg:text-7xl leading-[0.95] tracking-tight group-hover:text-[#C84C35] transition-colors duration-700">
                  {other.firstName} <span className="italic-accent">{other.lastName}</span>
                </h2>
                <div className="text-[#66635C] group-hover:text-[#C84C35] transition-colors duration-700 pt-2 shrink-0">
                  <ArrowUpRight size={40} strokeWidth={1} />
                </div>
              </div>
            </div>
          </Link>
        </section>
      )}
    </div>
  );
}
